import { type FC, useState } from "react";
import { FileText, Tag } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { PhoneNumberInput } from "@/shared/components/ui/phone-number-input";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { WHY_WE_ASK_TITLE } from "@/shared/constants/whyWeAsk";

interface ClientInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface ClientInfoStepProps {
  clientInfo: ClientInfo;
  onChange: (info: ClientInfo) => void;
  onNext: () => void;
  firstClientOfferAvailable: boolean;
}

export const ClientInfoStep: FC<ClientInfoStepProps> = ({
  clientInfo,
  onChange,
  onNext,
  firstClientOfferAvailable,
}) => {
  const [errors, setErrors] = useState<Partial<Record<keyof ClientInfo, string>>>({});

  const setField = (field: keyof ClientInfo, value: string) => {
    onChange({ ...clientInfo, [field]: value });
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleContinue = () => {
    const newErrors: Partial<Record<keyof ClientInfo, string>> = {};

    if (!clientInfo.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!clientInfo.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }
    if (!clientInfo.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientInfo.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onNext();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-1 flex-col gap-6 overflow-y-auto bg-subtle p-6">
        <div className="flex flex-col gap-1">
          <h4 className="text-xl font-semibold leading-[30px]">Invite a client</h4>
          <p className="text-sm text-secondary">
            Start your client&apos;s estate record in under a minute
          </p>
        </div>

        {firstClientOfferAvailable && (
          <div className="flex items-start gap-3 rounded-lg border border-brand bg-brand-subtle p-4">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
              <Tag className="h-4 w-4 text-brand" />
            </div>
            <p className="text-sm leading-[1.55]">
              <span className="font-semibold">First-client offer:</span> your first client&apos;s
              Estate Setup and Asset Search are on us, a $3,000 value. IRS transcript access
              included.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="wizard-first-name">First name</Label>
              <Input
                id="wizard-first-name"
                className="rounded-sm"
                value={clientInfo.firstName}
                onChange={(e) => setField("firstName", e.target.value)}
                placeholder="First name"
              />
              <FieldError message={errors.firstName} />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="wizard-last-name">Last name</Label>
              <Input
                id="wizard-last-name"
                className="rounded-sm"
                value={clientInfo.lastName}
                onChange={(e) => setField("lastName", e.target.value)}
                placeholder="Last name"
              />
              <FieldError message={errors.lastName} />
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="wizard-email">Email address</Label>
              <Input
                id="wizard-email"
                type="email"
                className="rounded-sm"
                value={clientInfo.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="Email address"
              />
              <FieldError message={errors.email} />
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="wizard-phone">
                Phone number <span className="text-tertiary">(optional)</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipInfoTrigger
                      aria-label={WHY_WE_ASK_TITLE}
                      className="ml-1 align-middle"
                    />
                    <TooltipContent title={WHY_WE_ASK_TITLE}>
                      {
                        "A phone number lets us reach your client by text if the invitation email goes unopened. Email alone works if you'd rather not add one."
                      }
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <PhoneNumberInput
                id="wizard-phone"
                className="rounded-sm"
                value={clientInfo.phone}
                onChange={(value) => setField("phone", value)}
                placeholder="(123) 123-1234"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 shrink-0 text-secondary" />
          <p className="text-sm leading-[1.55] text-secondary">
            If you already hold documents or details for this estate, invite your client to create
            the record, then add what you have. They start from where you left off, not from
            scratch.
          </p>
        </div>
      </div>

      <div className="flex justify-end border-t border-primary px-6 py-4">
        <Button variant="brand" onClick={handleContinue}>
          Continue
        </Button>
      </div>
    </div>
  );
};
